import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, IconButton, Paper, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography, CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Save as SaveIcon,
  Receipt as TaxIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import ZipTaxRatesPanel from './ZipTaxRatesPanel';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const buildEmptyRow = (st) => ({
  stateCode: st.code,
  stateName: st.name,
  taxRate: 0,
  taxable: false,
  effectiveDate: '',
  notes: '',
});

// For the data-loading callbacks, so a language switch doesn't refetch the rates. State names
// stay as they are: they are place names, and they are also sent to the API as stateName.
const tTax = (key, options) => i18n.t(key, { ns: 'taxRates', ...options });

const TaxRateManagementPage = () => {
  const { t } = useTranslation('taxRates');
  const { getAccessToken } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { code, field }
  const [editValue, setEditValue] = useState('');
  const [dirty, setDirty] = useState(new Set());

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error(tTax('errors.authRequired'));
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/tax-rates`, { headers });
      if (!res.ok) throw new Error(tTax('errors.fetchRates'));
      const data = await res.json();
      const serverRates = Array.isArray(data) ? data : data.data || [];

      const rateMap = {};
      serverRates.forEach((r) => { rateMap[r.stateCode] = r; });

      const merged = US_STATES.map((st) => {
        const existing = rateMap[st.code];
        if (existing) return {
          ...existing,
          stateName: st.name,
          taxable: existing.isTaxable ?? existing.taxable ?? false,
        };
        return buildEmptyRow(st);
      });
      setRows(merged);
      setDirty(new Set());
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const startEdit = (code, field, current) => {
    setEditingCell({ code, field });
    setEditValue(current ?? '');
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const { code, field } = editingCell;
    setRows((prev) =>
      prev.map((r) => {
        if (r.stateCode !== code) return r;
        const val = field === 'taxRate' ? parseFloat(editValue) || 0 : editValue;
        return { ...r, [field]: val };
      }),
    );
    setDirty((prev) => new Set(prev).add(code));
    setEditingCell(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditingCell(null);
  };

  const toggleTaxable = (code) => {
    setRows((prev) =>
      prev.map((r) => (r.stateCode === code ? { ...r, taxable: !r.taxable } : r)),
    );
    setDirty((prev) => new Set(prev).add(code));
  };

  const handleBulkUpdate = async () => {
    if (dirty.size === 0) { toast.info(t('toast.noChanges')); return; }
    setSaving(true);
    try {
      const headers = await authHeaders();
      const today = new Date().toISOString().split('T')[0];
      const updates = rows.filter((r) => dirty.has(r.stateCode)).map((r) => ({
        stateCode: r.stateCode,
        stateName: r.stateName,
        taxRate: r.taxRate,
        isTaxable: r.taxable,
        effectiveDate: r.effectiveDate || today,
        notes: r.notes || null,
      }));

      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/tax-rates/bulk`, {
        method: 'POST', headers,
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(translateServerMessage(err.message, t('errors.bulkUpdateFailed')));
      }
      toast.success(t('toast.updated', { count: updates.length }));
      setDirty(new Set());
      fetchRates();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDateChange = (code, value) => {
    setRows((prev) =>
      prev.map((r) => (r.stateCode === code ? { ...r, effectiveDate: value } : r)),
    );
    setDirty((prev) => new Set(prev).add(code));
  };

  const renderEditableCell = (row, field, display) => {
    if (field === 'effectiveDate') {
      return (
        <TextField
          size="small"
          type="date"
          value={row.effectiveDate || ''}
          onChange={(e) => handleDateChange(row.stateCode, e.target.value)}
          sx={{ width: 160 }}
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    const isEditing = editingCell?.code === row.stateCode && editingCell?.field === field;
    if (isEditing) {
      return (
        <TextField
          size="small" autoFocus value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit} onKeyDown={handleKeyDown}
          type={field === 'taxRate' ? 'number' : 'text'}
          sx={{ width: field === 'taxRate' ? 90 : 140 }}
          inputProps={field === 'taxRate' ? { step: '0.01', min: '0' } : {}}
        />
      );
    }
    return (
      <Box
        onClick={() => startEdit(row.stateCode, field, field === 'taxRate' ? row.taxRate : row[field])}
        sx={{ cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' }, minWidth: 40 }}
      >
        {display}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{t('title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subtitle')}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {dirty.size > 0 && (
            <Chip label={t('unsavedChanges', { count: dirty.size })}
              color="warning" size="small" />
          )}
          <Tooltip title={t('refresh')}>
            <IconButton onClick={fetchRates} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleBulkUpdate} disabled={dirty.size === 0 || saving}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {saving ? t('saving') : t('saveChanges')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700, width: 70 }}>{t('table.code')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('table.state')}</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120 }}>{t('table.taxRate')}</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 100 }}>{t('table.taxable')}</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 160 }}>{t('table.effectiveDate')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('table.notes')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <TaxIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">{t('table.empty')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => {
                  const modified = dirty.has(r.stateCode);
                  return (
                    <TableRow key={r.stateCode} hover
                      sx={modified ? { bgcolor: 'warning.50', borderLeft: '3px solid', borderLeftColor: 'warning.main' } : {}}>
                      <TableCell>
                        <Chip label={r.stateCode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{r.stateName}</TableCell>
                      <TableCell>
                        {renderEditableCell(r, 'taxRate', `${(r.taxRate ?? 0).toFixed(2)}%`)}
                      </TableCell>
                      <TableCell>
                        <Switch size="small" checked={r.taxable ?? false}
                          onChange={() => toggleTaxable(r.stateCode)} color="success" />
                      </TableCell>
                      <TableCell>
                        {renderEditableCell(r, 'effectiveDate', r.effectiveDate || '—')}
                      </TableCell>
                      <TableCell>
                        {renderEditableCell(r, 'notes', r.notes || '—')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <ZipTaxRatesPanel />
    </Box>
  );
};

export default TaxRateManagementPage;
